import type { HubResponseProps } from '@readme/iso';

import loadable from '@loadable/component';
import React from 'react';

import useIsPreviewMode from '@core/hooks/useIsPreviewMode';
import { InitializeSuperHubStore, useSuperHubStore } from '@core/store';

/**
 * Code-splitting the EditContainer and ViewContainer components to avoid
 * loading unnecessary JS when loading the non-admin version of the SuperHub.
 */
const EditContainer = loadable(() => import('./EditContainer'), { ssr: true });
const ViewContainer = loadable(() => import('./ViewContainer'), { ssr: true });

interface SuperHubProps extends HubResponseProps {
  children: React.ReactNode;
}

function Content(props: SuperHubProps) {
  const [isSuperHub, isSuperHubAdminOrEditor] = useSuperHubStore(s => [
    s.isSuperHub,
    s.user.role === 'admin' || (s.user.role === 'editor' && s.isSuperHubMergePermissionsEnabled),
  ]);
  const { isPreviewMode } = useIsPreviewMode();

  return isSuperHubAdminOrEditor && !isPreviewMode ? (
    <EditContainer {...props} />
  ) : isSuperHub ? (
    <ViewContainer {...props} />
  ) : (
    // For non-superhub projects, render content as is.
    <>{props.children}</>
  );
}

/**
 * Top-level wrapper provides our required context that all sub-components need.
 * This allows our top-level SuperHub component to also access context values.
 */
export default function SuperHub(props: SuperHubProps) {
  return (
    <InitializeSuperHubStore currentBranch={props.branch} currentVersion={props.gitVersion}>
      <Content {...props} />
    </InitializeSuperHubStore>
  );
}
